import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import {
  FiDollarSign,
  FiTrendingUp,
  FiMinusCircle,
  FiClock,
  FiRefreshCw,
} from "react-icons/fi";

import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import './analytics.css';

const pieColors = ["#5f1b2d", "#92263f", "#b5304a", "#f07a89", "#f7b955"];

function currentMonthValue() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;

  return `${year}-${month}`;
}

function getMonthRange(monthValue) {
  const [year, month] = monthValue.split("-").map(Number);

  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();

  return {
    from: `${year}-${String(month).padStart(2, "0")}-01`,
    to: `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(
      2,
      "0",
    )}`,
  };
}

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function formatMonth(month) {
  if (!month) return "—";

  const [year, monthNumber] = month.split("-").map(Number);

  return new Intl.DateTimeFormat("en-IN", {
    month: "short",
    year: "2-digit",
  }).format(new Date(year, monthNumber - 1, 1));
}

function displayStatus(status) {
  return String(status || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function responseList(response, key) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.[key])) return response[key];
  if (Array.isArray(response?.data)) return response.data;

  return [];
}

export default function PayrollReport() {
  const { accessToken } = useAuth();

  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);
  const [department, setDepartment] = useState("All");
  const [status, setStatus] = useState("All");

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    if (!accessToken || !selectedMonth) return;

    setIsLoading(true);
    setError("");

    try {
      const { from, to } = getMonthRange(selectedMonth);

      const response = await apiRequest(
        `/reports/payroll?from=${encodeURIComponent(
          from,
        )}&to=${encodeURIComponent(to)}`,
        { accessToken },
      );

      setReport(response || null);
    } catch (requestError) {
      setReport(null);
      setError(requestError.message || "Unable to load payroll report.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedMonth]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const payrollItems = useMemo(
    () => responseList(report, "payrollItems"),
    [report],
  );

  const departmentOptions = useMemo(() => {
    return [
      "All",
      ...Array.from(
        new Set(payrollItems.map((item) => item.department).filter(Boolean)),
      ).sort(),
    ];
  }, [payrollItems]);

  const filteredPayrollItems = useMemo(() => {
    return payrollItems.filter((item) => {
      const matchesDepartment =
        department === "All" || item.department === department;

      const matchesStatus = status === "All" || item.status === status;

      return matchesDepartment && matchesStatus;
    });
  }, [payrollItems, department, status]);

  const chartData = useMemo(() => {
    const currentTrend = responseList(report, "payrollTrend");
    const previousTrend = responseList(report, "previousPayrollTrend");

    const chartMap = new Map();

    currentTrend.forEach((item) => {
      chartMap.set(item.month, {
        month: formatMonth(item.month),
        current: Number(item.payroll || 0),
        previous: 0,
      });
    });

    previousTrend.forEach((item) => {
      const key = item.month;

      if (chartMap.has(key)) {
        chartMap.set(key, {
          ...chartMap.get(key),
          previous: Number(item.payroll || 0),
        });
        return;
      }

      chartMap.set(key, {
        month: formatMonth(key),
        current: 0,
        previous: Number(item.payroll || 0),
      });
    });

    return Array.from(chartMap.values());
  }, [report]);

  const salaryDistribution = useMemo(
    () => responseList(report, "salaryDistribution"),
    [report],
  );

  const metrics = report?.metrics || {};
  const comparison = report?.comparison || {};
  const previousMetrics = comparison?.previous || {};
  const changes = comparison?.changes || {};

  const overtimeAmount =
    salaryDistribution.find((item) => item.name === "Overtime")?.value || 0;


  return (
    <div className="charms-analytics space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payroll Analytics</h1>

          <p className="mt-2 text-gray-500">
            Analyze payroll performance, salary distribution and payouts.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={loadReport}
            disabled={isLoading}
            className="h-12 w-12 rounded-2xl border border-gray-200 bg-white text-gray-600 flex items-center justify-center hover:bg-gray-50 disabled:opacity-60"
            title="Refresh report"
          >
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} />
          </button>

        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <input
            type="month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            className="px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />

          <select
            value={department}
            onChange={(event) => setDepartment(event.target.value)}
            className="px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            {departmentOptions.map((item) => (
              <option key={item} value={item}>
                {item === "All" ? "All Departments" : item}
              </option>
            ))}
          </select>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="All">All Statuses</option>
            <option value="POSTED">Posted</option>
            <option value="PAID">Paid</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <ReportCard
          title="Gross Payroll"
          value={formatCurrency(metrics.grossPayroll)}
          subtitle={comparisonText(
            changes.grossPayroll,
            previousMetrics.grossPayroll,
          )}
          icon={<FiDollarSign size={22} />}
          iconBg="bg-primary-50"
          iconColor="text-primary-700"
        />

        <ReportCard
          title="Net Payroll"
          value={formatCurrency(metrics.netPayroll)}
          subtitle={comparisonText(
            changes.netPayroll,
            previousMetrics.netPayroll,
          )}
          icon={<FiTrendingUp size={22} />}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />

        <ReportCard
          title="Deductions"
          value={formatCurrency(metrics.deductions)}
          subtitle={comparisonText(
            changes.deductions,
            previousMetrics.deductions,
          )}
          icon={<FiMinusCircle size={22} />}
          iconBg="bg-red-50"
          iconColor="text-red-600"
        />

        <ReportCard
          title="Overtime"
          value={formatCurrency(overtimeAmount)}
          subtitle={`${Number(metrics.paidEmployees || 0)} employees paid`}
          icon={<FiClock size={22} />}
          iconBg="bg-yellow-50"
          iconColor="text-yellow-600"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Payroll Trend
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Current period compared with the same period last year.
              </p>
            </div>

            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1.5 text-primary-700">
                <span className="h-2 w-2 rounded-full bg-primary-700" />
                Current
              </span>

              <span className="flex items-center gap-1.5 text-gray-500">
                <span className="h-2 w-2 rounded-full bg-gray-400" />
                Last Year
              </span>
            </div>
          </div>

          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `₹${value / 1000}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />

                <Line
                  type="monotone"
                  dataKey="current"
                  name="Current Payroll"
                  stroke="#92263f"
                  strokeWidth={3}
                  dot={{ r: 4 }}
                />

                <Line
                  type="monotone"
                  dataKey="previous"
                  name="Last Year Payroll"
                  stroke="#9ca3af"
                  strokeWidth={2}
                  strokeDasharray="7 6"
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Salary Distribution
          </h3>

          <p className="text-sm text-gray-500 mb-5">
            Earnings and deductions breakdown.
          </p>

          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={salaryDistribution}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={88}
                  label={({ percent }) =>
                    `${Math.round(Number(percent || 0) * 100)}%`
                  }
                >
                  {salaryDistribution.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={pieColors[index % pieColors.length]}
                    />
                  ))}
                </Pie>

                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-2">
            {salaryDistribution.map((item, index) => (
              <div
                key={item.name}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <span className="flex items-center gap-2 text-gray-600">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{
                      backgroundColor: pieColors[index % pieColors.length],
                    }}
                  />
                  {item.name}
                </span>

                <span className="font-semibold text-gray-900">
                  {formatCurrency(item.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Payroll Details
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              {filteredPayrollItems.length} payroll item
              {filteredPayrollItems.length === 1 ? "" : "s"} found.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Employee
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Gross
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Deductions
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Net Salary
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    Loading payroll report…
                  </td>
                </tr>
              ) : filteredPayrollItems.length ? (
                filteredPayrollItems.map((employee) => (
                  <tr
                    key={employee.id}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-5">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {employee.name}
                        </p>

                        <p className="text-sm text-gray-500">
                          {employee.employeeCode}
                        </p>
                      </div>
                    </td>

                    <td className="px-6 py-5 text-gray-600">
                      {employee.department || "Unassigned"}
                    </td>

                    <td className="px-6 py-5 font-medium">
                      {formatCurrency(employee.gross)}
                    </td>

                    <td className="px-6 py-5 text-red-600 font-medium">
                      {formatCurrency(employee.deductions)}
                    </td>

                    <td className="px-6 py-5 font-semibold text-primary-700">
                      {formatCurrency(employee.net)}
                    </td>

                    <td className="px-6 py-5">
                      <StatusBadge status={employee.status} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-16 text-center text-gray-500"
                  >
                    No posted payroll records found for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function comparisonText(change, previousValue) {
  const numericChange = Number(change || 0);

  if (!Number(previousValue || 0)) {
    return "No data last year";
  }

  return `${numericChange >= 0 ? "+" : ""}${numericChange.toFixed(
    1,
  )}% vs last year`;
}

function ReportCard({ title, value, subtitle, icon, iconBg, iconColor }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>

          <h3 className="mt-3 text-3xl font-bold text-gray-900">{value}</h3>

          <p className="mt-2 text-sm text-gray-400">{subtitle}</p>
        </div>

        <div
          className={`h-14 w-14 rounded-2xl flex items-center justify-center ${iconBg} ${iconColor}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    PAID: "bg-green-50 text-green-700 border-green-100",
    POSTED: "bg-blue-50 text-blue-700 border-blue-100",
    DRAFT: "bg-yellow-50 text-yellow-700 border-yellow-100",
    VOIDED: "bg-red-50 text-red-700 border-red-100",
  };

  return (
    <span
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
        styles[status] || "bg-gray-50 text-gray-700 border-gray-100"
      }`}
    >
      {displayStatus(status)}
    </span>
  );
}
